import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { MapPin, Navigation, Search, SlidersHorizontal, X, AlertCircle, List } from 'lucide-react';
import { ProviderCard } from '../components/ProviderCard';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { Logo } from '../components/Logo';
import { useAuth } from '../context/AuthContext';
import { useGeolocation } from '../hooks/useGeolocation';
import { searchProvidersByLocation, getAllProviders } from '../services/api';
import { ProviderSearchResult } from '../types';
import { CARE_TYPES, ROUTES } from '../constants';
import { trackSearch } from '../utils/analytics';

export const FindProviders: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  if (user?.role === 'admin') {
    return <Navigate to={ROUTES.ADMIN_DASHBOARD} replace />;
  }
  const [searchParams] = useSearchParams();
  const { latitude, longitude, error: geoError, loading: geoLoading, getCurrentPosition, clearError } = useGeolocation();

  useEffect(() => {
    if (user?.role === 'user' && !user.isProfileCompleted) {
      navigate(ROUTES.HOME_PAGE, { replace: true });
    }
  }, [user, navigate]);

  const [searchMode, setSearchMode] = useState<'gps' | 'manual' | 'browse'>(() => {
    const mode = searchParams.get('mode');
    return mode === 'browse' ? 'browse' : 'gps';
  });
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [radiusMiles, setRadiusMiles] = useState(20);
  const [selectedCareType, setSelectedCareType] = useState('');
  const [searchText, setSearchText] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [providers, setProviders] = useState<ProviderSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProviders, setTotalProviders] = useState(0);
  const [resultsPerPage, setResultsPerPage] = useState(12);

  const [searchCenter, setSearchCenter] = useState<{ latitude: number; longitude: number } | null>(null);

  useEffect(() => {
    const savedLocation = localStorage.getItem('lastSearchLocation');
    if (savedLocation) {
      const { latitude, longitude } = JSON.parse(savedLocation);
      setSearchCenter({ latitude, longitude });
    }
  }, []);

  const handleGPSSearch = async () => {
    if (!latitude || !longitude) {
      getCurrentPosition();
      return;
    }

    setLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      const result = await searchProvidersByLocation({
        latitude,
        longitude,
        radiusMiles,
        typeOfCare: selectedCareType || undefined,
      });

      if (result.success) {
        const nonBlockedProviders = result.providers.filter(p => !p.isBlocked);
        setProviders(nonBlockedProviders);
        if (result.searchCenter) {
          setSearchCenter(result.searchCenter);
          localStorage.setItem('lastSearchLocation', JSON.stringify(result.searchCenter));
        }
        trackSearch('GPS Location Search', {
          content_category: selectedCareType || 'All Care Types',
        });
      } else {
        setError(result.message || 'Failed to search providers');
      }
    } catch (err) {
      setError('An error occurred while searching for providers');
    } finally {
      setLoading(false);
    }
  };

  const handleManualSearch = async () => {
    if (!postalCode || !city) {
      setError('Please enter both postal code and county/city');
      return;
    }

    setLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      const result = await searchProvidersByLocation({
        postalCode,
        city,
        radiusMiles,
        typeOfCare: selectedCareType || undefined,
      });

      if (result.success) {
        const nonBlockedProviders = result.providers.filter(p => !p.isBlocked);
        setProviders(nonBlockedProviders);
        if (result.searchCenter) {
          setSearchCenter(result.searchCenter);
          localStorage.setItem('lastSearchLocation', JSON.stringify(result.searchCenter));
        }
        trackSearch(`${postalCode} ${city}`, {
          content_category: selectedCareType || 'All Care Types',
        });
      } else {
        setError(result.message || 'Failed to search providers');
      }
    } catch (err) {
      setError('An error occurred while searching for providers');
    } finally {
      setLoading(false);
    }
  };

  const handleBrowseAll = async (page: number = 1) => {
    setLoading(true);
    setError(null);
    setHasSearched(true);
    setSearchCenter(null);

    try {
      const result = await getAllProviders({
        page,
        limit: resultsPerPage,
        typeOfCare: selectedCareType || undefined,
        search: searchText || undefined,
      });

      if (result.success) {
        const nonBlockedProviders = result.providers.filter(p => !p.isBlocked);
        setProviders(nonBlockedProviders);
        setCurrentPage(result.currentPage);
        setTotalPages(result.totalPages);
        setTotalProviders(result.totalProviders || 0);
        trackSearch(searchText || 'Browse All Providers', {
          content_category: selectedCareType || 'All Care Types',
        });
      } else {
        setError(result.message || 'Failed to fetch providers');
      }
    } catch (err) {
      setError('An error occurred while fetching providers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (latitude && longitude && searchMode === 'gps') {
      handleGPSSearch();
    }
  }, [latitude, longitude]);

  useEffect(() => {
    if (searchMode === 'browse') {
      handleBrowseAll(1);
    }
  }, [searchMode, selectedCareType, resultsPerPage]);

  const handleClearFilters = () => {
    setSelectedCareType('');
    setRadiusMiles(20);
    setSearchText('');
    setPostalCode('');
    setCity('');
  };

  const handleViewDetails = (providerId: string) => {
    navigate(`${ROUTES.PROVIDER_DETAIL}/${providerId}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Logo />
            <Button variant="outline" onClick={() => navigate(ROUTES.HOME_PAGE)}>
              Back to Dashboard
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Find Care Providers</h1>
          <p className="text-gray-600">Search for qualified care providers near you</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Search Filters</h2>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              <SlidersHorizontal className="w-4 h-4 mr-2" />
              {showFilters ? 'Hide' : 'Show'} Filters
            </Button>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={() => setSearchMode('gps')}
                className={`p-4 rounded-lg border-2 transition-colors ${
                  searchMode === 'gps'
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <Navigation className="w-6 h-6 mx-auto mb-2 text-blue-600" />
                <p className="font-medium text-gray-900">Use My Location</p>
                <p className="text-sm text-gray-600">Find providers near me</p>
              </button>

              <button
                onClick={() => setSearchMode('manual')}
                className={`p-4 rounded-lg border-2 transition-colors ${
                  searchMode === 'manual'
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <MapPin className="w-6 h-6 mx-auto mb-2 text-blue-600" />
                <p className="font-medium text-gray-900">Enter Address</p>
                <p className="text-sm text-gray-600">Search by location</p>
              </button>

              <button
                onClick={() => {
                  setSearchMode('browse');
                  setShowFilters(true);
                }}
                className={`p-4 rounded-lg border-2 transition-colors ${
                  searchMode === 'browse'
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <List className="w-6 h-6 mx-auto mb-2 text-blue-600" />
                <p className="font-medium text-gray-900">Browse All</p>
                <p className="text-sm text-gray-600">View all providers</p>
              </button>
            </div>

            {searchMode === 'gps' && (
              <div>
                {geoError && (
                  <div className="flex items-start gap-2 p-4 bg-red-50 border border-red-200 rounded-lg mb-4">
                    <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm text-red-800">{geoError}</p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          clearError();
                          setSearchMode('manual');
                        }}
                        className="mt-2"
                      >
                        Use Manual Address Instead
                      </Button>
                    </div>
                  </div>
                )}
                <Button
                  onClick={handleGPSSearch}
                  disabled={geoLoading}
                  className="w-full"
                >
                  {geoLoading ? (
                    <>
                      <LoadingSpinner size="sm" />
                      <span className="ml-2">Getting Location...</span>
                    </>
                  ) : latitude && longitude ? (
                    <>
                      <Search className="w-4 h-4 mr-2" />
                      Search Near Me
                    </>
                  ) : (
                    <>
                      <Navigation className="w-4 h-4 mr-2" />
                      Enable Location
                    </>
                  )}
                </Button>
              </div>
            )}

            {searchMode === 'manual' && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Postal Code"
                    value={postalCode}
                    onChange={(e) => setPostalCode(e.target.value)}
                    placeholder="e.g., SW1A 1AA"
                  />
                  <Input
                    label="County / City"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="e.g., London or Greater Manchester"
                  />
                </div>
                <Button onClick={handleManualSearch} disabled={loading} className="w-full">
                  {loading ? (
                    <>
                      <LoadingSpinner size="sm" />
                      <span className="ml-2">Searching...</span>
                    </>
                  ) : (
                    <>
                      <Search className="w-4 h-4 mr-2" />
                      Search Providers
                    </>
                  )}
                </Button>
              </div>
            )}

            {(showFilters || searchMode === 'browse') && (
              <div className="pt-4 border-t border-gray-200 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Type of Care
                    </label>
                    <select
                      value={selectedCareType}
                      onChange={(e) => setSelectedCareType(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">All Care Types</option>
                      {CARE_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>

                  {searchMode === 'browse' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Results Per Page
                      </label>
                      <select
                        value={resultsPerPage}
                        onChange={(e) => setResultsPerPage(Number(e.target.value))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="12">12 per page</option>
                        <option value="24">24 per page</option>
                        <option value="36">36 per page</option>
                        <option value="48">48 per page</option>
                      </select>
                    </div>
                  )}
                </div>

                {searchMode !== 'browse' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Search Radius: {radiusMiles} miles
                    </label>
                    <input
                      type="range"
                      min="5"
                      max="50"
                      step="5"
                      value={radiusMiles}
                      onChange={(e) => setRadiusMiles(Number(e.target.value))}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-gray-600 mt-1">
                      <span>5 miles</span>
                      <span>50 miles</span>
                    </div>
                  </div>
                )}

                <div>
                  <Input
                    label="Search by Name"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    placeholder="Search provider name..."
                  />
                </div>

                {searchMode === 'browse' && (
                  <Button onClick={() => handleBrowseAll(1)} className="w-full">
                    <Search className="w-4 h-4 mr-2" />
                    Apply Filters
                  </Button>
                )}

                {searchMode !== 'browse' && (
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={handleClearFilters} className="flex-1">
                      <X className="w-4 h-4 mr-2" />
                      Clear Filters
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {loading && (
          <div className="flex justify-center items-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        )}

        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <p className="text-red-800">{error}</p>
            </div>
          </div>
        )}

        {!loading && hasSearched && providers.length === 0 && (
          <div className="text-center py-12">
            <MapPin className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Providers Found</h3>
            <p className="text-gray-600 mb-4">
              Try expanding your search radius or adjusting your filters
            </p>
            <Button onClick={() => setRadiusMiles(radiusMiles + 10)}>
              Expand Search Radius
            </Button>
          </div>
        )}

        {!loading && providers.length > 0 && (
          <>
            <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
              <div>
                {searchMode === 'browse' ? (
                  <div>
                    <p className="text-gray-700">
                      Showing <span className="font-semibold">{providers.length}</span> of{' '}
                      <span className="font-semibold">{totalProviders}</span> provider{totalProviders !== 1 ? 's' : ''}
                    </p>
                    <p className="text-sm text-gray-600">
                      Page {currentPage} of {totalPages}
                    </p>
                  </div>
                ) : (
                  <p className="text-gray-700">
                    Found <span className="font-semibold">{providers.length}</span> provider{providers.length !== 1 ? 's' : ''}
                    {searchCenter && ` near you`}
                  </p>
                )}
              </div>

              {selectedCareType && (
                <div className="flex items-center gap-2">
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                    {selectedCareType}
                  </span>
                  <button
                    onClick={() => setSelectedCareType('')}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {providers.map((provider) => (
                <ProviderCard
                  key={provider.id}
                  provider={provider}
                  onViewDetails={handleViewDetails}
                />
              ))}
            </div>

            {searchMode === 'browse' && totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 flex-wrap">
                <Button
                  variant="outline"
                  onClick={() => handleBrowseAll(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>

                <div className="flex gap-1">
                  {currentPage > 2 && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleBrowseAll(1)}
                      >
                        1
                      </Button>
                      {currentPage > 3 && <span className="px-2 py-2 text-gray-500">...</span>}
                    </>
                  )}

                  {currentPage > 1 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleBrowseAll(currentPage - 1)}
                    >
                      {currentPage - 1}
                    </Button>
                  )}

                  <Button size="sm" className="bg-blue-600 text-white">
                    {currentPage}
                  </Button>

                  {currentPage < totalPages && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleBrowseAll(currentPage + 1)}
                    >
                      {currentPage + 1}
                    </Button>
                  )}

                  {currentPage < totalPages - 1 && (
                    <>
                      {currentPage < totalPages - 2 && <span className="px-2 py-2 text-gray-500">...</span>}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleBrowseAll(totalPages)}
                      >
                        {totalPages}
                      </Button>
                    </>
                  )}
                </div>

                <Button
                  variant="outline"
                  onClick={() => handleBrowseAll(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};
